import "katex/dist/katex.min.css";
import { Loader2Icon, PlusSquare } from "lucide-react";
import { isValidElement, type ReactNode } from "react";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import { useAiChatOptional } from "~/ai-chat/feature/use-ai-chat";
import { parseEntityLink } from "~/ai-chat/model/entity-link";
import type { FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import type { MeshViewRequest } from "~/ai-chat/model/mesh-file-types";
import {
  parseSaveResourceAction,
  SAVE_RESOURCE_LANGUAGE,
  type SaveResourceAction,
} from "~/ai-chat/model/save-resource-action";
import { ChatEntityChip } from "~/ai-chat/ui/chat-entity-chip";
import { cn } from "~/utils/cn";

export type RenderSaveResourceAction = (action: SaveResourceAction, blockKey: number) => ReactNode;

interface ChatMarkdownProps {
  content: string;
  isStreaming?: boolean;
  renderSaveResourceAction?: RenderSaveResourceAction;
  onViewMesh?: (mesh: MeshViewRequest) => void;
  onPreview?: (file: FilePreviewRequest) => void;
}

const HEADING_CLASSES = "mt-2 mb-0.5 text-sm leading-snug font-semibold first:mt-0 last:mb-0";

export function ChatMarkdown({
  content,
  isStreaming = false,
  renderSaveResourceAction,
  onViewMesh,
  onPreview,
}: ChatMarkdownProps) {
  const chat = useAiChatOptional();

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        p({ children, ...props }) {
          return (
            <p className="my-1 first:mt-0 last:mb-0" {...props}>
              {children}
            </p>
          );
        },
        ul({ children, ...props }) {
          return (
            <ul className="my-1 list-disc pl-4 first:mt-0 last:mb-0" {...props}>
              {children}
            </ul>
          );
        },
        ol({ children, ...props }) {
          return (
            <ol className="my-1 list-decimal pl-4 first:mt-0 last:mb-0" {...props}>
              {children}
            </ol>
          );
        },
        li({ children, ...props }) {
          return (
            <li className="my-0 leading-snug" {...props}>
              {children}
            </li>
          );
        },
        h1({ children, ...props }) {
          return (
            <h1 className={HEADING_CLASSES} {...props}>
              {children}
            </h1>
          );
        },
        h2({ children, ...props }) {
          return (
            <h2 className={HEADING_CLASSES} {...props}>
              {children}
            </h2>
          );
        },
        h3({ children, ...props }) {
          return (
            <h3 className={HEADING_CLASSES} {...props}>
              {children}
            </h3>
          );
        },
        h4({ children, ...props }) {
          return (
            <h4 className={HEADING_CLASSES} {...props}>
              {children}
            </h4>
          );
        },
        h5({ children, ...props }) {
          return (
            <h5 className={HEADING_CLASSES} {...props}>
              {children}
            </h5>
          );
        },
        h6({ children, ...props }) {
          return (
            <h6 className={HEADING_CLASSES} {...props}>
              {children}
            </h6>
          );
        },
        blockquote({ children, ...props }) {
          return (
            <blockquote
              className="border-muted-foreground/40 text-muted-foreground my-1 border-l-2 pl-2 italic first:mt-0 last:mb-0"
              {...props}
            >
              {children}
            </blockquote>
          );
        },
        hr(props) {
          return <hr className="border-border my-1.5" {...props} />;
        },
        strong({ children, ...props }) {
          return (
            <strong className="font-semibold" {...props}>
              {children}
            </strong>
          );
        },
        em({ children, ...props }) {
          return (
            <em className="italic" {...props}>
              {children}
            </em>
          );
        },
        a({ children, href, ...props }) {
          const entityLink = href ? parseEntityLink(href) : null;
          if (entityLink) {
            return (
              <ChatEntityChip link={entityLink} onViewMesh={onViewMesh} onPreview={onPreview}>
                {children}
              </ChatEntityChip>
            );
          }
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
              {...props}
            >
              {children}
            </a>
          );
        },
        code({ className, children, ...props }) {
          const isBlock = /\blanguage-/.test(className ?? "") || String(children).includes("\n");
          if (isBlock) {
            const codeString = String(children).replace(/\n$/, "");
            return (
              <code className={cn("font-mono text-xs", className)} {...props}>
                {codeString}
              </code>
            );
          }
          return (
            <code
              className={cn("bg-background/60 rounded px-1 py-0.5 font-mono text-[0.8125rem] break-all", className)}
              {...props}
            >
              {children}
            </code>
          );
        },
        pre({ node, children, ...props }) {
          const saveResourceCode = saveResourceCodeOf(children);
          if (saveResourceCode !== null) {
            // Mid-stream the fence is incomplete (possibly unclosed, partial
            // JSON) — show a placeholder instead of flashing raw JSON, and only
            // parse once the message has finished streaming.
            if (isStreaming) return <SaveResourcePendingBlock />;
            const action = parseSaveResourceAction(saveResourceCode);
            if (action !== null && renderSaveResourceAction) {
              return renderSaveResourceAction(action, node?.position?.start.offset ?? 0);
            }
          }

          const codeChild = children as React.ReactElement<{ className?: string; children?: string }>;
          const lang = codeChild?.props?.className ?? "";
          const isPython = /language-python/.test(lang);
          const codeText = typeof codeChild?.props?.children === "string" ? codeChild.props.children : null;
          const showInsert = Boolean(isPython && codeText && chat?.onInsertCode);

          return (
            <div className="relative my-1 first:mt-0 last:mb-0">
              <pre
                className={cn(
                  "bg-background/60 max-w-full overflow-x-auto rounded-md p-1.5 text-xs whitespace-pre",
                  // Room for the overlaid Insert button, so a line's tail can scroll clear of it.
                  showInsert && "pr-16",
                )}
                {...props}
              >
                {children}
              </pre>
              {isPython && codeText && chat?.onInsertCode && (
                <button
                  type="button"
                  onClick={() => chat.onInsertCode?.(codeText)}
                  className={cn(
                    "absolute top-1 right-1 flex items-center gap-1 rounded px-1.5 py-0.5",
                    "bg-background/80 text-muted-foreground hover:text-foreground",
                    "text-[0.7rem] leading-none transition-colors",
                  )}
                  aria-label="Insert code into active cell"
                >
                  <PlusSquare className="size-3" />
                  Insert
                </button>
              )}
            </div>
          );
        },
        table({ children, ...props }) {
          return (
            <div className="my-1 overflow-x-auto first:mt-0 last:mb-0">
              <table className="border-border w-full border-collapse border text-xs" {...props}>
                {children}
              </table>
            </div>
          );
        },
        th({ children, ...props }) {
          return (
            <th className="bg-background/40 border-border border px-1.5 py-0.5 text-left font-semibold" {...props}>
              {children}
            </th>
          );
        },
        td({ children, ...props }) {
          return (
            <td className="border-border border px-1.5 py-0.5" {...props}>
              {children}
            </td>
          );
        },
      }}
    >
      {content}
    </Markdown>
  );
}

function saveResourceCodeOf(children: ReactNode): string | null {
  if (!isValidElement(children)) return null;
  const codeProps = children.props as { className?: string; children?: ReactNode };
  if (!codeProps.className?.includes(`language-${SAVE_RESOURCE_LANGUAGE}`)) return null;
  const code = codeProps.children;
  if (typeof code === "string") return code;
  if (Array.isArray(code) && code.every((part) => typeof part === "string")) return code.join("");
  return null;
}

function SaveResourcePendingBlock() {
  return (
    <div className="border-border bg-card my-1 flex items-center gap-2 rounded-lg border p-2.5 first:mt-0 last:mb-0">
      <Loader2Icon className="text-muted-foreground size-3.5 motion-safe:animate-spin" aria-hidden="true" />
      <span className="text-muted-foreground text-xs">Preparing resource…</span>
    </div>
  );
}
